import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useMemo } from 'react';
import type { AxiosError } from 'axios';
import AppLayout from '../components/AppLayout';
import SettingsHelpSidebar from '../components/SettingsHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import { formatDate, utcMs } from '../utils/dateFormat';
import { API_ENDPOINTS } from '../constants/api';
import SyncIcon from '@mui/icons-material/Sync';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
  RecalculateWorkingDaysResultDialog,
  type RecalculateWorkingDaysResult,
} from '../components/RecalculateWorkingDaysResultDialog';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSave, genericCopy } from '../utils/userFacingCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { savedSuccessfully } from '../utils/successCopy';
import { SortableTableHeader } from '../utils/sortableTableHeader';

interface ReviewCycleSettings {
  useCalendarYear: boolean;
  autoCreateCycles: boolean;
  autoStartCycles: boolean;
  autoCreateThresholdPercent: number;
  defaultDeadlineDaysBeforeEnd: number;
  allowPeerReviews: boolean;
  minPeerReviewers: number;
  maxPeerReviewers: number;
  fiscalYearStartMonth: number;
  fiscalYearStartDay: number;
  governanceComplianceText?: string;
  emailOptOutAcknowledgmentText?: string;
  checkInOptOutAcknowledgmentText?: string;
  /** No longer used; kept so API response and PUT payload remain valid. */
  minHoursAfterLastPeerApprovalBeforeFinalise?: number;
}

type PublicHolidayYearSyncRow = { year: number; syncedAtUtc: string };
type PublicHolidayRow = { date: string; name: string };
type PublicHolidayYearSyncSortField = 'year' | 'syncedAtUtc';
type PublicHolidayRowSortField = 'date' | 'name';

function parseHolidayDateYear(isoDate: string): number | null {
  if (!isoDate || isoDate.length < 4) return null;
  const y = parseInt(isoDate.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

function formatHolidaySyncError(err: unknown): string {
  const fromApi = getApiErrorMessage(err, '');
  if (fromApi) return fromApi;
  const axiosError = err as AxiosError;
  const status = axiosError.response?.status;
  if (status === 401 || status === 403) {
    return "You don't have permission to refresh holidays. If you think this is wrong, sign in again or contact support.";
  }
  if (status === 502) {
    return "The holiday service couldn't be reached. Try again in a few minutes.";
  }
  if (!axiosError.response) {
    return "We couldn't connect. Check your network and try again.";
  }
  return 'Something went wrong while refreshing holidays. Try again, or contact support if it keeps happening.';
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { displayTimeZoneId } = useAuth();
  const showSuccess = useSuccessSnackbar();
  const { showLegacyHelp } = useAgentContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [dbHolidayDataset, setDbHolidayDataset] = useState<{
    holidays: PublicHolidayRow[];
    yearSyncs: PublicHolidayYearSyncRow[];
  } | null>(null);
  const [dbHolidayDatasetLoading, setDbHolidayDatasetLoading] = useState(false);
  const [dbHolidayDatasetError, setDbHolidayDatasetError] = useState<string | null>(null);
  const [supportedCountries, setSupportedCountries] = useState<Array<{ code: string; name: string }>>([]);
  const [countryTab, setCountryTab] = useState<string>('ZA');
  const [yearSyncSortField, setYearSyncSortField] = useState<PublicHolidayYearSyncSortField>('year');
  const [yearSyncSortDirection, setYearSyncSortDirection] = useState<'asc' | 'desc'>('asc');
  const [holidaySortField, setHolidaySortField] = useState<PublicHolidayRowSortField>('date');
  const [holidaySortDirection, setHolidaySortDirection] = useState<'asc' | 'desc'>('asc');
  const [holidayPage, setHolidayPage] = useState(0);
  const [holidayRowsPerPage, setHolidayRowsPerPage] = useState(25);
  /** When set, the holiday list below shows only rows whose date falls in this calendar year. Toggle off by clicking the same year again. */
  const [holidayListFilterYear, setHolidayListFilterYear] = useState<number | null>(null);
  const [holidayDatasetRefreshKey, setHolidayDatasetRefreshKey] = useState(0);
  const [holidaySyncLoading, setHolidaySyncLoading] = useState(false);
  const [holidaySyncError, setHolidaySyncError] = useState('');
  const [holidaySyncSuccess, setHolidaySyncSuccess] = useState('');
  const [recalcConfirmOpen, setRecalcConfirmOpen] = useState(false);
  const [recalcResultOpen, setRecalcResultOpen] = useState(false);
  const [recalcResult, setRecalcResult] = useState<RecalculateWorkingDaysResult | null>(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcError, setRecalcError] = useState('');
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'settings',
    pageTitle: 'Settings',
    visibleSections: ['review-cycle-settings', 'governance', 'country-and-holidays'],
    suggestedPrompts: [
      'What does each setting control?',
      'What should I configure before starting a cycle?',
      'Explain governance compliance text usage.',
      'What do Sync from Nager and recalculate leave working days do?',
      'How do the public holiday tables and year filter work?',
    ],
  });
  const [settings, setSettings] = useState<ReviewCycleSettings>({
    useCalendarYear: true,
    autoCreateCycles: true,
    autoStartCycles: true,
    autoCreateThresholdPercent: 75,
    defaultDeadlineDaysBeforeEnd: 7,
    allowPeerReviews: false,
    minPeerReviewers: 2,
    maxPeerReviewers: 4,
    fiscalYearStartMonth: 10,
    fiscalYearStartDay: 1,
    governanceComplianceText: '',
    emailOptOutAcknowledgmentText: '',
    checkInOptOutAcknowledgmentText: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.get<ReviewCycleSettings>('/System/review-cycle-settings');
        if (cancelled) return;
        setSettings(response.data);
      } catch (err: unknown) {
        if (cancelled) return;
                setError(getApiErrorMessage(err, couldNotLoad('settings')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDbHolidayDatasetLoading(true);
    setDbHolidayDatasetError(null);
    apiService
      .get<{
        holidays: PublicHolidayRow[];
        yearSyncs: PublicHolidayYearSyncRow[];
      }>(`${API_ENDPOINTS.SYSTEM.PUBLIC_HOLIDAY_DATASET}?country=${countryTab}`)
      .then((res) => {
        if (cancelled) return;
        setDbHolidayDataset(res.data);
        setDbHolidayDatasetError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDbHolidayDataset(null);
        setDbHolidayDatasetError(
          getApiErrorMessage(err, couldNotLoad('stored holiday data'))
        );
      })
      .finally(() => {
        if (!cancelled) setDbHolidayDatasetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [countryTab, holidayDatasetRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    apiService.get<{ countries: Array<{ code: string; name: string }> }>('/System/supported-countries')
      .then((res) => {
        if (!cancelled) setSupportedCountries(res.data.countries);
      })
      .catch(() => {
        if (!cancelled) setSupportedCountries([{ code: 'ZA', name: 'South Africa' }, { code: 'GB', name: 'United Kingdom' }]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setHolidayPage(0);
    setHolidayListFilterYear(null);
  }, [dbHolidayDataset]);

  useEffect(() => {
    setHolidayPage(0);
  }, [holidayListFilterYear]);

  const handleSyncPublicHolidays = async () => {
    setHolidaySyncError('');
    setHolidaySyncSuccess('');
    try {
      setHolidaySyncLoading(true);
      await apiService.post(`${API_ENDPOINTS.SYSTEM.PUBLIC_HOLIDAY_SYNC}?country=${countryTab}`, {});
      setHolidayDatasetRefreshKey((k) => k + 1);
      setHolidaySyncSuccess(
        'Holidays were updated from Nager.Date. The tables below have refreshed with the latest dates and names.'
      );
    } catch (err: unknown) {
      setHolidaySyncError(formatHolidaySyncError(err));
    } finally {
      setHolidaySyncLoading(false);
    }
  };

  const handleRunRecalculateWorkingDays = async () => {
    setRecalcConfirmOpen(false);
    setRecalcError('');
    try {
      setRecalcLoading(true);
      const response = await apiService.post<RecalculateWorkingDaysResult>(API_ENDPOINTS.LEAVE.RECALCULATE_WORKING_DAYS, {});
      setRecalcResult(response.data);
      setRecalcResultOpen(true);
    } catch (err: unknown) {
            setRecalcError(getApiErrorMessage(err, genericCopy.recalculation));
    } finally {
      setRecalcLoading(false);
    }
  };

  const sortedYearSyncs = useMemo(() => {
    const rows = dbHolidayDataset?.yearSyncs;
    if (!rows?.length) return [];
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp: number;
      if (yearSyncSortField === 'year') {
        cmp = a.year - b.year;
      } else {
        cmp = utcMs(a.syncedAtUtc) - utcMs(b.syncedAtUtc);
      }
      return yearSyncSortDirection === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [dbHolidayDataset, yearSyncSortField, yearSyncSortDirection]);

  const sortedHolidays = useMemo(() => {
    const rows = dbHolidayDataset?.holidays;
    if (!rows?.length) return [];
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp: number;
      if (holidaySortField === 'date') {
        cmp = a.date.localeCompare(b.date);
      } else {
        cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      }
      return holidaySortDirection === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [dbHolidayDataset, holidaySortField, holidaySortDirection]);

  const filteredSortedHolidays = useMemo(() => {
    if (holidayListFilterYear == null) return sortedHolidays;
    return sortedHolidays.filter((h) => {
      const y = parseHolidayDateYear(h.date);
      return y === holidayListFilterYear;
    });
  }, [sortedHolidays, holidayListFilterYear]);

  const paginatedHolidays = useMemo(() => {
    const start = holidayPage * holidayRowsPerPage;
    return filteredSortedHolidays.slice(start, start + holidayRowsPerPage);
  }, [filteredSortedHolidays, holidayPage, holidayRowsPerPage]);

  const handleYearSyncSort = (field: PublicHolidayYearSyncSortField) => {
    if (yearSyncSortField === field) {
      setYearSyncSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setYearSyncSortField(field);
      setYearSyncSortDirection('asc');
    }
  };

  const handleHolidaySort = (field: PublicHolidayRowSortField) => {
    if (holidaySortField === field) {
      setHolidaySortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setHolidaySortField(field);
      setHolidaySortDirection('asc');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await apiService.put('/System/review-cycle-settings', settings);
      showSuccess(savedSuccessfully('Settings'));
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotSave('settings')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SettingsIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Settings
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Configure system settings: date and locale (country and public holidays), review cycle behaviour (calendar vs fiscal year, auto-create and auto-start), and review policy (peer reviews).
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Review Cycle Configuration
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.useCalendarYear}
                    onChange={(e) => setSettings({ ...settings, useCalendarYear: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      Use Calendar Year
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, review cycles align with calendar year quarters (Q1: Jan-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec).
                      When disabled, cycles use fiscal year quarters (configurable start date).
                    </Typography>
                  </Box>
                }
              />

              {!settings.useCalendarYear && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                      label="Fiscal Year Start Month"
                      type="number"
                      value={settings.fiscalYearStartMonth}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value >= 1 && value <= 12) {
                          setSettings({ ...settings, fiscalYearStartMonth: value });
                        }
                      }}
                      helperText="Month when fiscal year starts (1-12)"
                      inputProps={{ min: 1, max: 12 }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Fiscal Year Start Day"
                      type="number"
                      value={settings.fiscalYearStartDay}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value >= 1 && value <= 31) {
                          setSettings({ ...settings, fiscalYearStartDay: value });
                        }
                      }}
                      helperText="Day of month when fiscal year starts (1-31)"
                      inputProps={{ min: 1, max: 31 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </>
              )}

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoCreateCycles}
                    onChange={(e) => setSettings({ ...settings, autoCreateCycles: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      Auto-Create Review Cycles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically create review cycles when the current cycle reaches the threshold percentage.
                    </Typography>
                  </Box>
                }
              />

              {settings.autoCreateCycles && (
                <>
                  <TextField
                    label="Auto-Create Threshold (%)"
                    type="number"
                    value={settings.autoCreateThresholdPercent}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 0 && value <= 100) {
                        setSettings({ ...settings, autoCreateThresholdPercent: value });
                      }
                    }}
                    helperText="Create next cycle when current cycle is this percentage complete (0-100)"
                    inputProps={{ min: 0, max: 100 }}
                    fullWidth
                  />
                </>
              )}

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoStartCycles}
                    onChange={(e) => setSettings({ ...settings, autoStartCycles: e.target.checked })}
                    disabled={!settings.autoCreateCycles}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      Auto-Start Cycles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Automatically start cycles when they are created. This creates reviews for all active users immediately.
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <TextField
                label="Default employee cutoff (days before end)"
                type="number"
                value={settings.defaultDeadlineDaysBeforeEnd}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 0) {
                    setSettings({ ...settings, defaultDeadlineDaysBeforeEnd: value });
                  }
                }}
                helperText="Number of days before cycle end to set the employee cutoff (date by which employees must complete their part)."
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Public Holidays by Country
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and sync public holidays by supported country. Leave calculations use each member&apos;s business unit country.
            </Typography>
            {countryTab === 'IN' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Indian employees observe the <strong>South Africa</strong> public holiday calendar. The tables below show those dates.
              </Alert>
            )}
            <Tabs value={countryTab} onChange={(_, value) => setCountryTab(value)} sx={{ mb: 2 }}>
              {(supportedCountries.length > 0 ? supportedCountries : [
                { code: 'ZA', name: 'South Africa' },
                { code: 'GB', name: 'United Kingdom' },
                { code: 'IN', name: 'India' },
              ]).map((c) => (
                <Tab key={c.code} value={c.code} label={`${c.name} (${c.code})`} />
              ))}
            </Tabs>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Public holidays
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Holidays for the country above come from{' '}
              <Link href="https://date.nager.at" target="_blank" rel="noopener noreferrer">
                Nager.Date
              </Link>
              . They&apos;re used when choosing dates and when leave is worked out. The tables show what we&apos;re using
              and when each year was last refreshed. <strong>Click a year</strong> in the first table to filter the list
              below; click it again to show every year.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={holidaySyncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                  disabled={holidaySyncLoading}
                  onClick={handleSyncPublicHolidays}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Sync from Nager now
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0, m: 0, pt: 0 }}
                >
                  Pulls the latest public holidays from Nager for the selected country tab. Recent and upcoming years. Use this if the official calendar changed and you want everything up to
                  date.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={recalcLoading ? <CircularProgress size={16} /> : <CalculateIcon />}
                  disabled={recalcLoading}
                  onClick={() => {
                    setRecalcError('');
                    setRecalcConfirmOpen(true);
                  }}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Recalculate all leave working days
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0, m: 0, pt: 0 }}
                >
                  Updates working-day totals on all leave using each member&apos;s business unit country and public holiday calendar.
                  Run this after holiday syncs, or
                  if day counts look wrong. Also available from the{' '}
                  <MuiLink component={RouterLink} to="/leave/audit-log" underline="hover">
                    Leave audit log
                  </MuiLink>
                  .
                </Typography>
              </Box>
            </Box>
            {holidaySyncLoading && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Updating holidays from Nager.Date—this may take a few seconds.
              </Alert>
            )}
            {holidaySyncSuccess && !holidaySyncLoading && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setHolidaySyncSuccess('')}>
                {holidaySyncSuccess}
              </Alert>
            )}
            {holidaySyncError && !holidaySyncLoading && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHolidaySyncError('')}>
                {holidaySyncError}
              </Alert>
            )}
            {recalcError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecalcError('')}>
                {recalcError}
              </Alert>
            )}
            <Dialog open={recalcConfirmOpen} onClose={() => !recalcLoading && setRecalcConfirmOpen(false)}>
              <DialogTitle>Recalculate working days for all leave?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  We&apos;ll recalculate working days on every leave booking using the country and public holidays on this
                  page. Any change is recorded in the audit trail; you can see the detail on the Leave audit log after it
                  runs.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRecalcConfirmOpen(false)} disabled={recalcLoading}>
                  Cancel
                </Button>
                <Button onClick={handleRunRecalculateWorkingDays} color="warning" variant="contained" disabled={recalcLoading}>
                  Run
                </Button>
              </DialogActions>
            </Dialog>
            <RecalculateWorkingDaysResultDialog
              open={recalcResultOpen}
              onClose={() => setRecalcResultOpen(false)}
              result={recalcResult}
            />
            {dbHolidayDatasetLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Loading stored dataset…</Typography>
              </Box>
            ) : dbHolidayDatasetError ? (
              <Alert severity="warning">{dbHolidayDatasetError}</Alert>
            ) : dbHolidayDataset ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {sortedYearSyncs.length > 0 && (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small" aria-label="Public holiday sync by calendar year" key={displayTimeZoneId}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <SortableTableHeader
                                label="Calendar year"
                                active={yearSyncSortField === 'year'}
                                direction={yearSyncSortField === 'year' ? yearSyncSortDirection : 'asc'}
                                onClick={() => handleYearSyncSort('year')}
                              />
                            </TableCell>
                            <TableCell>
                              <SortableTableHeader
                                label="Last synced (UTC)"
                                active={yearSyncSortField === 'syncedAtUtc'}
                                direction={yearSyncSortField === 'syncedAtUtc' ? yearSyncSortDirection : 'asc'}
                                onClick={() => handleYearSyncSort('syncedAtUtc')}
                              />
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedYearSyncs.map((y) => (
                            <TableRow
                              key={y.year}
                              hover
                              selected={holidayListFilterYear === y.year}
                              onClick={() =>
                                setHolidayListFilterYear((prev) => (prev === y.year ? null : y.year))
                              }
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{y.year}</TableCell>
                              <TableCell>
                                {formatDate(y.syncedAtUtc, { includeTime: true })}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
                {dbHolidayDataset.holidays.length > 0 ? (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    {holidayListFilterYear != null && (
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                        Showing holidays for <strong>{holidayListFilterYear}</strong> only
                        {' · '}
                        <MuiLink
                          component="button"
                          type="button"
                          onClick={() => setHolidayListFilterYear(null)}
                          sx={{ verticalAlign: 'baseline', fontSize: 'inherit' }}
                        >
                          Show all years
                        </MuiLink>
                      </Typography>
                    )}
                    <TableContainer>
                      <Table size="small" aria-label="Stored public holidays">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <SortableTableHeader
                                label="Date"
                                active={holidaySortField === 'date'}
                                direction={holidaySortField === 'date' ? holidaySortDirection : 'asc'}
                                onClick={() => handleHolidaySort('date')}
                              />
                            </TableCell>
                            <TableCell>
                              <SortableTableHeader
                                label="Name"
                                active={holidaySortField === 'name'}
                                direction={holidaySortField === 'name' ? holidaySortDirection : 'asc'}
                                onClick={() => handleHolidaySort('name')}
                              />
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedHolidays.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography variant="body2" color="text.secondary">
                                  {holidayListFilterYear != null
                                    ? `No holidays stored for ${holidayListFilterYear} in this dataset.`
                                    : 'No rows to display.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedHolidays.map((h) => (
                              <TableRow key={h.date + h.name} hover>
                                <TableCell>{h.date}</TableCell>
                                <TableCell>{h.name}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={filteredSortedHolidays.length}
                      page={holidayPage}
                      onPageChange={(_, newPage) => setHolidayPage(newPage)}
                      rowsPerPage={holidayRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setHolidayRowsPerPage(parseInt(e.target.value, 10));
                        setHolidayPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                    />
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No rows in <code>PublicHolidays</code> yet. Sync runs when someone uses leave features or after the daily background task.
                  </Typography>
                )}
              </Box>
            ) : null}
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Governance and Compliance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Wording shown to users for mandatory post-login acknowledgment. When set, users must acknowledge this text before using the app (annual reset and when text changes).
            </Typography>
            <TextField
              label="Governance and Compliance text"
              multiline
              minRows={6}
              value={settings.governanceComplianceText ?? ''}
              onChange={(e) => setSettings({ ...settings, governanceComplianceText: e.target.value })}
              fullWidth
              placeholder="e.g. Data processing (GDPR/POPIA/AI Act), secure use, and data rights..."
            />
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Email opt-out acknowledgment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Wording shown when someone turns off all optional emails, and again if they must reaffirm after you change this text. Changing it requires opted-out users to reaffirm on next login (or turn emails back on).
            </Typography>
            <TextField
              label="Email opt-out acknowledgment text"
              multiline
              minRows={4}
              value={settings.emailOptOutAcknowledgmentText ?? ''}
              onChange={(e) => setSettings({ ...settings, emailOptOutAcknowledgmentText: e.target.value })}
              fullWidth
              placeholder="Turning off optional emails does not relieve you of your responsibilities…"
            />
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Check-in opt-out acknowledgment
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Points shown when a manager turns off built-in check-ins (one point per line). Changing this text requires opted-out managers to reaffirm on next login (or turn built-in check-ins back on).
            </Typography>
            <TextField
              label="Check-in opt-out acknowledgment points"
              multiline
              minRows={5}
              value={settings.checkInOptOutAcknowledgmentText ?? ''}
              onChange={(e) => setSettings({ ...settings, checkInOptOutAcknowledgmentText: e.target.value })}
              fullWidth
              placeholder={'One acknowledgment point per line…'}
            />
          </CardContent>
        </Card>

        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Review Policy Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              These settings apply system-wide to all review cycles. They define who can perform reviews and the review process structure.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowPeerReviews}
                    onChange={(e) => setSettings({ ...settings, allowPeerReviews: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="500">
                      Allow Peer Reviews
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When enabled, employees can nominate peers to rate selected objectives; peers are invited when the employee submits their self-review, and managers can add peer reviewers from the review. When disabled, only Team Leads and Managers can perform reviews.
                    </Typography>
                  </Box>
                }
              />

              {settings.allowPeerReviews && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Minimum Peer Reviewers"
                    type="number"
                    value={settings.minPeerReviewers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 1) {
                        setSettings({ ...settings, minPeerReviewers: value });
                      }
                    }}
                    helperText="Minimum number of peer reviewers required (at least 1)"
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Maximum Peer Reviewers"
                    type="number"
                    value={settings.maxPeerReviewers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= settings.minPeerReviewers) {
                        setSettings({ ...settings, maxPeerReviewers: value });
                      }
                    }}
                    helperText={`Maximum number of peer reviewers allowed (must be >= ${settings.minPeerReviewers})`}
                    inputProps={{ min: settings.minPeerReviewers }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Settings'}
          </Button>
        </Box>
      </Box>
      {showLegacyHelp && (
        <SettingsHelpSidebar isExpanded={helpExpanded} onToggle={() => setHelpExpanded(!helpExpanded)} />
      )}
    </AppLayout>
  );
}
